import React from 'react';
import { Factory, Tag, AlertTriangle, Layers } from 'lucide-react';
import { ProductThumb } from './ProductThumb';
import { GROUPS, TYPE_LABEL, allLines, tablesFor, lineStatus, fromPrice, money, isFactory } from './catalog';

const L = {
  'pt-BR': { windows: 'Janelas', shapes: 'Formas especiais', doors: 'Portas', legacy: 'Tipos antigos (aberturas já medidas)', from: 'a partir de', factory: 'fábrica', simple: 'direto', flat: 'fixo', all: 'todas as linhas', none: 'sem preço', tables: (n) => `${n} tabelas`, edit: 'Editar preços' },
  en: { windows: 'Windows', shapes: 'Special shapes', doors: 'Doors', legacy: 'Legacy types (already measured openings)', from: 'from', factory: 'factory', simple: 'direct', flat: 'flat', all: 'all lines', none: 'no price', tables: (n) => `${n} tables`, edit: 'Edit prices' },
};
const CHIP = {
  factory: 'bg-teal-50 border-teal-200 text-teal-800',
  simple: 'bg-sky-50 border-sky-200 text-sky-800',
  flat: 'bg-sky-50 border-sky-200 text-sky-800',
  all: 'bg-slate-100 border-slate-200 text-slate-700',
  none: 'bg-amber-50 border-dashed border-amber-300 text-amber-700',
};
const Icon = ({ kind }) => (kind === 'factory' ? <Factory className="h-3 w-3" /> : kind === 'none' ? <AlertTriangle className="h-3 w-3" /> : kind === 'all' ? <Layers className="h-3 w-3" /> : <Tag className="h-3 w-3" />);

const Card = ({ type, tables, lines, t, labels, onOpen }) => {
  const statuses = lines.map((l) => ({ line: l, ...lineStatus(tables, l.id) }));
  const covered = statuses.some((s) => s.kind === 'all');
  const shared = tables.find((p) => p.line == null && !isFactory(p));
  const first = tables.find(isFactory) || shared || tables[0];
  const price = fromPrice(first);
  const missing = statuses.filter((s) => s.kind === 'none').length;
  return (
    <button type="button" onClick={() => onOpen(type)} className={`group w-full min-w-0 overflow-hidden text-left rounded-2xl border bg-white p-3 flex gap-3 items-start hover:shadow-md hover:-translate-y-0.5 transition-[transform,box-shadow] focus:outline-none focus:ring-2 focus:ring-teal-500/40 ${missing ? 'border-amber-200' : 'border-slate-200'}`} data-testid={`catalog-card-${type}`} title={t.edit}>
      <div className="h-[68px] w-[84px] rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center shrink-0 group-hover:bg-teal-50/60 transition-colors"><ProductThumb type={type} size={76} testId={`catalog-thumb-${type}`} /></div>
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex items-baseline gap-2 min-w-0"><p className="font-semibold text-sm truncate" data-testid={`catalog-name-${type}`}>{labels[type] || type}</p><span className="text-[10px] font-mono text-slate-400 shrink-0">{type}</span></div>
        <p className="text-xs text-slate-600" data-testid={`catalog-from-${type}`}>{price ? <>{t.from} <span className="font-semibold text-slate-900">{money(price)}</span></> : <span className="text-amber-700 font-semibold">{t.none}</span>}{tables.length > 1 ? <span className="text-slate-400"> · {t.tables(tables.length)}</span> : null}</p>
        <div className="flex flex-wrap gap-1">
          {statuses.filter((s) => s.kind !== 'all').map((s) => (
            <span key={s.line.id} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${CHIP[s.kind]}`} data-testid={`catalog-line-${type}-${s.line.id}`} data-kind={s.kind}>
              <Icon kind={s.kind} /> {lines.length > 1 && statuses.length > 2 ? `${s.line.brand} ${s.line.name}` : s.line.name}{s.kind === 'factory' && s.n > 1 ? ` ×${s.n}` : ''}{s.kind === 'none' ? ` · ${t.none}` : ''}
            </span>
          ))}
          {covered && <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${CHIP.all}`} data-testid={`catalog-line-${type}-all`} data-kind="all"><Icon kind="all" /> {t.all} · {shared?.mode === 'flat' ? t.flat : t.simple}</span>}
        </div>
      </div>
    </button>
  );
};

// Catálogo visual: um card por produto, agrupado em Janelas / Formas / Portas. Clique abre o editor da(s) tabela(s) do produto.
export const CatalogGrid = ({ products = [], brands = [], onOpen, language = 'pt-BR' }) => {
  const t = L[language] || L['pt-BR'];
  const labels = TYPE_LABEL[language] || TYPE_LABEL['pt-BR'];
  const lines = allLines(brands);
  return (
    <div className="space-y-6" data-testid="catalog-grid">
      {GROUPS.map((g) => {
        const types = g.types;
        return (
          <section key={g.id} data-testid={`catalog-group-${g.id}`} className={g.id === 'legacy' ? 'opacity-80' : ''}>
            <h3 className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 mb-2.5">{t[g.id]}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 [&>*]:min-w-0">
              {types.map((type) => <Card key={type} type={type} tables={tablesFor(products, type)} lines={lines} t={t} labels={labels} onOpen={onOpen} />)}
            </div>
          </section>
        );
      })}
    </div>
  );
};
